/**
 * Display B-esper logo in browser console at application startup
 * Following best practices from Anthropic, DEV, and industry standards
 */

// B-Esper Brand Colors (hardcoded to avoid import issues)
const BESPER_COLORS = {
  primary: '#5897de',
  primaryDark: '#022d54',
  accent: '#ffbc82',
  textSecondary: '#4a5568'
};

/**
 * Synchronous version that displays immediately - ALWAYS runs first
 * This is called at the very top of index.js before any other code
 * Uses CSS styling for browser console compatibility
 * NO DEPENDENCIES - uses hardcoded colors to avoid import issues
 */
export function displayStartupLogoSync() {
  try {
    // B-Esper Brand Colors (hardcoded to avoid import dependencies)
    const PRIMARY_DARK = '#022d54';
    const PRIMARY = '#5897de';
    const ACCENT = '#ffbc82';
    
    // Large B-esper ASCII art with hyphen
    const largeLogo = [
      '',
      '    ██████╗        ███████╗███████╗██████╗ ███████╗██████╗ ',
      '    ██╔══██╗       ██╔════╝██╔════╝██╔══██╗██╔════╝██╔══██╗',
      '    ██████╔╝ █████╗███████╗███████╗██████╔╝█████╗  ██████╔╝',
      '    ██╔══██╗ ╚════╝██╔════╝╚════██║██╔═══╝ ██╔══╝  ██╔══██╗',
      '    ██████╔╝       ███████╗███████║██║     ███████╗██║  ██║',
      '    ╚═════╝        ╚══════╝╚══════╝╚═╝     ╚══════╝╚═╝  ╚═╝',
      ''
    ];

    // CSS style with white-space: pre to preserve newlines and better spacing
    const logoStyle = `color: ${ACCENT}; font-family: 'Courier New', monospace; font-size: 11px; line-height: 1.2; white-space: pre; letter-spacing: 0.3px;`;
    
    // Display logo with CSS styling
    console.log(`%c${largeLogo.join('\n')}`, logoStyle);
    
    // Get environment info (use defaults if not available yet)
    const env = (typeof window !== 'undefined' && window._env_?.enviroment) || '';
    const branch = (typeof window !== 'undefined' && window._env_?.BRANCH_CODE) || 'unknown';
    const deployTimestamp = (typeof window !== 'undefined' && window._env_?.DEPLOY_TIMESTAMP) || null;
    
    // Display text info with CSS styling - more beautiful
    const titleStyle = `color: ${ACCENT}; font-size: 16px; font-weight: 700; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; letter-spacing: 1px; text-shadow: 0 0 10px rgba(255, 188, 130, 0.3);`;
    const infoStyle = `color: ${PRIMARY}; font-size: 13px; font-family: 'Courier New', monospace; font-weight: 500;`;
    const separatorStyle = `color: ${PRIMARY_DARK}; font-family: monospace; opacity: 0.6;`;
    
    console.log('%cCustomer Portal', titleStyle);
    console.log(`%cEnvironment: ${env}  |  Branch: ${branch}`, infoStyle);
    
    // Log deployment timestamp for non-prod environments with "time ago"
    if (env !== 'prod' && deployTimestamp) {
      try {
        // Parse deployment timestamp (format: "2026-01-19 13:06:56 CET")
        const deployDate = new Date(deployTimestamp.replace(' CET', ''));
        const now = new Date();
        const diffMs = now - deployDate;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        let timeAgo = '';
        if (diffDays > 0) {
          timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        } else if (diffHours > 0) {
          timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        } else if (diffMins > 0) {
          timeAgo = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        } else {
          timeAgo = 'just now';
        }
        
        console.log(`%c📦 Deployed: ${deployTimestamp} (${timeAgo})`, infoStyle);
      } catch (e) {
        // Fallback if date parsing fails
        console.log(`%c📦 Deployed: ${deployTimestamp}`, infoStyle);
      }
    }
    
    // Beautiful separator line with decorative elements
    const separator = '═'.repeat(45);
    console.log(`%c${separator}`, separatorStyle);
    // Log in production when ?ppdebug in URL (so we see where stack overflow happens in built bundle)
    if (typeof window !== 'undefined' && (process.env.NODE_ENV === 'development' || (window.location && window.location.search && window.location.search.includes('ppdebug')))) {
      console.log('[PP-DBG] 1 displayStartupLogoSync done (sync, before React)');
    }
  } catch (e) {
    // Silently fail - don't break app initialization
    console.debug('Logo display error:', e);
  }
}

export function displayStartupLogo() {
  // Prevent double-render in React StrictMode and repeated calls
  if (sessionStorage.getItem('bsp_logo_shown_customer') === 'true') {
    return;
  }

  // Only display in development or with explicit flag
  const shouldDisplay = process.env.NODE_ENV === 'development' || 
                        window._env_?.SHOW_STARTUP_LOGO !== 'false';
  
  if (!shouldDisplay) return;

  try {
    // Create a temporary image element to get logo dimensions
    const img = new Image();
    img.onload = function() {
      const aspectRatio = this.width / this.height;
      const displayHeight = 60;
      const displayWidth = Math.round(displayHeight * aspectRatio);
      
      // Logo styling - displays image in console (keep CSS for image)
      const logoStyles = [
        'font-size: 1px',
        `padding: ${displayHeight}px ${displayWidth}px`,
        'background-image: url("/logo192.png")',
        'background-size: contain',
        'background-repeat: no-repeat',
        'background-position: center',
        `line-height: ${displayHeight}px`
      ].join(';');

      // Display logo image
      console.log('%c ', logoStyles);

      // Display text info with CSS styling
      const titleStyle = `color: ${BESPER_COLORS.accent}; font-size: 14px; font-weight: bold; font-family: Arial, sans-serif;`;
      const infoStyle = `color: ${BESPER_COLORS.primary}; font-size: 12px; font-family: monospace;`;
      const separatorStyle = `color: ${BESPER_COLORS.primaryDark}; font-family: monospace;`;
      
      console.log('%cB-esper Customer Portal', titleStyle);
      console.log('%cApplication started successfully', infoStyle);
      
      // Environment info
      const env = window._env_?.enviroment || '';
      const branch = window._env_?.BRANCH_CODE || 'unknown';
      const envText = `enviroment: ${env} | Branch: ${branch}`;
      console.log(`%c${envText}`, `color: ${BESPER_COLORS.textSecondary}; font-size: 12px; font-family: monospace;`);
      
      // Separator with CSS color
      const separator = '─'.repeat(50);
      console.log(`%c${separator}`, separatorStyle);

      // Mark as shown
      sessionStorage.setItem('bsp_logo_shown_customer', 'true');
    };
    
    img.onerror = function() {
      // Fallback to ASCII art if image fails to load
      displayStartupLogoASCII();
      sessionStorage.setItem('bsp_logo_shown_customer', 'true');
    };
    
    img.src = '/logo192.png';
    
  } catch (error) {
    // Silently fail - don't break app if logo display fails
    console.debug('Logo display skipped:', error.message);
    displayStartupLogoASCII();
    sessionStorage.setItem('bsp_logo_shown_customer', 'true');
  }
}

// Alternative: ASCII Art Fallback
export function displayStartupLogoASCII() {
  const logoLines = [
    '  ╔═══════════════════════════════════════╗',
    '  ║                                       ║',
    '  ║         ██████╗        ███████╗       ║',
    '  ║         ██╔══██╗       ██╔════╝       ║',
    '  ║         ██████╔╝ █████╗█████╗         ║',
    '  ║         ██╔══██╗╚════╝██╔══╝         ║',
    '  ║         ██████╔╝      ███████╗       ║',
    '  ║         ╚═════╝       ╚══════╝       ║',
    '  ║                                       ║',
    '  ║           B-esper Platform            ║',
    '  ║                                       ║',
    '  ╚═══════════════════════════════════════╝'
  ];
  
  // Use CSS styling instead of ANSI for browser compatibility
  const logoStyle = `color: ${BESPER_COLORS.accent}; font-family: monospace; font-size: 11px; line-height: 1.2; white-space: pre;`;
  // eslint-disable-next-line no-unused-vars
  const titleStyle = `color: ${BESPER_COLORS.accent}; font-size: 14px; font-weight: bold; font-family: Arial, sans-serif;`;
  const infoStyle = `color: ${BESPER_COLORS.primary}; font-size: 12px; font-family: monospace;`;
  const separatorStyle = `color: ${BESPER_COLORS.primaryDark}; font-family: monospace;`;
  
  console.log(`%c${logoLines.join('\n')}`, logoStyle);
  
  const env = window._env_?.enviroment || '';
  const branch = window._env_?.BRANCH_CODE || 'unknown';
  const infoText = `Customer Portal | enviroment: ${env} | Branch: ${branch}`;
  console.log(`%c${infoText}`, infoStyle);
  
  const separator = '─'.repeat(70);
  console.log(`%c${separator}`, separatorStyle);
}

